import React from 'react';

/**
 * Bidirectional sync between a media element and a PM transcript with timed paragraphs.
 *
 * - Playback → activeParagraphIndex: updated every animationFrame while playing.
 * - Sentence click → seekTo(startMs): seeks the media element.
 *
 * pmContent: ProseMirror doc content array (paragraphs with attrs.startMs / attrs.endMs).
 * mediaRef: React ref to <audio>/<video> element.
 * src: stream URL. Transcript clicks only pass startMs, so seekTo must fall back
 *      to this when the media element has not been given a src yet (lazy Play).
 */
export default function useMediaTranscriptSync({pmContent, mediaRef, src: mediaSrc}) {
    const [activeParagraphIndex, setActiveParagraphIndex] = React.useState(-1);
    const rafRef = React.useRef(null);

    const paragraphs = React.useMemo(() => {
        if (!Array.isArray(pmContent)) return [];
        // Track each node's original top-level block index (== canonical segment numberInItem)
        // before filtering, so highlight create/display can key on the canonical index — not the
        // re-indexed, paragraph-only position.
        let filteredIndex = -1;
        return pmContent
            .map((node, blockIndex) => ({node, blockIndex}))
            .filter(({node}) => node?.type === 'paragraph' && node?.attrs?.startMs != null)
            .map(({node, blockIndex}) => {
                filteredIndex += 1;
                return {
                    index: filteredIndex,
                    blockIndex,
                    startMs: node.attrs.startMs,
                    endMs: node.attrs.endMs ?? node.attrs.startMs,
                    // Plain text for fallback / aria labels
                    text: node.content?.map((t) => t.text || '').join('') ?? '',
                    // Raw inline nodes preserved so transcript view can render entity marks
                    inlineNodes: node.content ?? [],
                };
            });
    }, [pmContent]);

    const findActiveIndex = React.useCallback((currentMs) => {
        if (!paragraphs.length) return -1;
        for (let i = paragraphs.length - 1; i >= 0; i--) {
            if (currentMs >= paragraphs[i].startMs) return i;
        }
        return -1;
    }, [paragraphs]);

    React.useEffect(() => {
        const media = mediaRef?.current;
        if (!media) return;

        const tick = () => {
            const ms = (media.currentTime || 0) * 1000;
            setActiveParagraphIndex(findActiveIndex(ms));
            if (!media.paused && !media.ended) {
                rafRef.current = requestAnimationFrame(tick);
            }
        };

        const onPlay = () => { rafRef.current = requestAnimationFrame(tick); };
        const onPause = () => { cancelAnimationFrame(rafRef.current); tick(); };
        const onSeeked = () => tick();

        media.addEventListener('play', onPlay);
        media.addEventListener('pause', onPause);
        media.addEventListener('seeked', onSeeked);

        return () => {
            cancelAnimationFrame(rafRef.current);
            media.removeEventListener('play', onPlay);
            media.removeEventListener('pause', onPause);
            media.removeEventListener('seeked', onSeeked);
        };
    }, [mediaRef, findActiveIndex]);

    const seekTo = React.useCallback((startMs, src, options = {}) => {
        const {autoplay = true} = options;
        const media = mediaRef?.current;
        if (!media) return;
        // SourcesTranscriptView calls onSentenceClick(startMs) only. The stream URL
        // therefore has to come from this hook (or an explicit second arg).
        const streamSrc = src || mediaSrc;
        // HAVE_NOTHING: setting currentTime is a silent no-op. Assign src if the
        // player is still cold (lazy Play never ran) and wait for canplay.
        if (media.error || media.readyState === 0) {
            if (streamSrc && !media.src) media.src = streamSrc;
            if (!media.src) return;
            media.load();
            media.addEventListener('canplay', () => {
                media.currentTime = startMs / 1000;
                if (autoplay) media.play().catch(() => {});
            }, {once: true});
            return;
        }
        media.currentTime = startMs / 1000;
        if (autoplay && media.paused && media.readyState >= 2) {
            media.play().catch(() => {});
        }
    }, [mediaRef, mediaSrc]);

    return {paragraphs, activeParagraphIndex, seekTo};
}
